import React from 'react';
import type { Crepe } from '@milkdown/crepe';
import type { Ctx } from '@milkdown/ctx';
import { renderToStaticMarkup } from 'react-dom/server';
import { MagicWand } from '@phosphor-icons/react';
import { BrainfishColors } from '@brainfish-ai/components';

import { createGrammarCheckHandler } from './grammarCheck';
import { grammarCheckDecorationPlugin } from './decoration';
import type { GrammarCheckConfig } from './grammarCheck';

export type GrammarCheckFeatureConfig = GrammarCheckConfig;

/**
 * Configure grammar check feature for the editor.
 * Installs the decoration plugin for loading animation.
 *
 * Usage:
 * ```ts
 * configureGrammarCheck(crepe);
 * ```
 */
export function configureGrammarCheck(crepe: Crepe): void {
  crepe.editor.use(grammarCheckDecorationPlugin);
}

type GrammarToolbarItem = {
  icon: string;
  active: (ctx: Ctx) => boolean;
  onRun: (ctx: Ctx) => void;
};

type ToolbarGroupHandle = {
  addItem: (key: string, item: GrammarToolbarItem) => unknown;
};

/**
 * Creates a grammar check toolbar button configuration.
 * Use this with Crepe's toolbar builder to add the grammar check button.
 *
 * @param onGrammarCheckRef - React ref containing the grammar check callback
 *
 * Usage:
 * ```ts
 * [Crepe.Feature.Toolbar]: {
 *   buildToolbar: (builder) => {
 *     const functionGroup = builder.getGroup('function');
 *     configureGrammarToolbarButton(functionGroup, onGrammarCheckRef);
 *   },
 * },
 * ```
 */
export function createGrammarToolbarButton(
  onGrammarCheckRef: React.RefObject<GrammarCheckConfig['onGrammarCheck'] | undefined>,
): GrammarToolbarItem {
  return {
    icon: renderToStaticMarkup(
      <MagicWand
        size={24}
        aria-label="Grammar check"
        style={{
          color: BrainfishColors.blue.DEFAULT,
          fill: BrainfishColors.blue.DEFAULT,
        }}
      />,
    ),

    active: (_ctx: Ctx) => false,
    onRun: (ctx: Ctx) => {
      if (!onGrammarCheckRef.current) {
        return;
      }

      const handler = createGrammarCheckHandler({
        onGrammarCheck: onGrammarCheckRef.current,
      });

      handler(ctx).catch((e) => {
        console.warn('Error in grammar check handler', e);
      });
    },
  };
}

/**
 * Adds the grammar check button to an existing toolbar group.
 * Convenience wrapper around createGrammarToolbarButton.
 *
 * Usage:
 * ```ts
 * [Crepe.Feature.Toolbar]: {
 *   buildToolbar: (builder) => {
 *     const functionGroup = builder.getGroup('function');
 *     configureGrammarToolbarButton(functionGroup, onGrammarCheckRef);
 *   },
 * },
 * ```
 */
export function configureGrammarToolbarButton(
  toolbarGroup: ToolbarGroupHandle,
  onGrammarCheckRef: React.RefObject<GrammarCheckConfig['onGrammarCheck'] | undefined>,
): void {
  toolbarGroup.addItem('grammarCheck', createGrammarToolbarButton(onGrammarCheckRef));
}
